import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { callApi, HttpMethod } from "src/services/apiService";
import { toast } from "sonner";
import Loading from "src/components/Loading";
import GoHighLevelNav from "./GoHighLevelNav";
import styles from "./GoHighLevel.module.css";

interface BulkRun {
  id: number;
  locationId: string;
  locationName: string;
  agencyName: string;
  status: number;
  processedCount: number | null;
  updatedCount: number | null;
  skippedCount: number | null;
  startedAt: string;
  completedAt: string | null;
  error: string | null;
}

interface Dashboard {
  agencies: { totalAgencies: number; installedAgencies: number };
  subAccounts: { totalSubAccounts: number; activeSubAccounts: number; totalContacts: number; lastContactRefresh: string | null };
  bulkScrubEnabled: number;
  runningNow: number;
  today: { singleScrubsToday: number; bulkCompletedToday: number; bulkProcessedToday: number; bulkUpdatedToday: number; bulkFailedToday: number };
  recentRuns: BulkRun[];
}

const STATUS_LABEL: Record<number, string> = { 0: "Queued", 1: "Processing", 2: "Completed", 3: "Failed" };
const STATUS_CLASS: Record<number, string> = { 0: "bg-secondary", 1: "bg-info", 2: "bg-success", 3: "bg-danger" };

const StatCard: React.FC<{
  value: React.ReactNode;
  label: string;
  sub?: string;
  onClick?: () => void;
  accent?: "accent" | "warn";
}> = ({ value, label, sub, onClick, accent }) => (
  <div
    className={`${styles.statCard} ${onClick ? styles.statCardClickable : ""}`}
    onClick={onClick}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
  >
    <div className={`${styles.statValue} ${accent === "accent" ? styles.statAccent : ""} ${accent === "warn" ? styles.statWarn : ""}`}>
      {value}
    </div>
    <div className={styles.statLabel}>{label}</div>
    {sub && <div className={styles.statSub}>{sub}</div>}
  </div>
);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setData(await callApi("sysadmin/gohighlevel/dashboard", HttpMethod.GET));
      } catch {
        toast.error("Failed to load dashboard.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <Loading />;
  if (!data) return null;

  const scrubbedToday = data.today.singleScrubsToday + data.today.bulkProcessedToday;

  return (
    <div className={styles.container}>
      <GoHighLevelNav />
      <div className={styles.pageHeader}>
        <div>
          <h2 className={styles.pageTitle}>GoHighLevel Dashboard</h2>
          <p className={styles.pageSubtitle}>
            Contact counts refreshed {data.subAccounts.lastContactRefresh ? new Date(data.subAccounts.lastContactRefresh).toLocaleString() : "never"}
          </p>
        </div>
      </div>

      <div className={styles.statGrid}>
        <StatCard
          value={data.agencies.totalAgencies.toLocaleString()}
          label="Agencies"
          sub={`${data.agencies.installedAgencies} with app installed`}
          onClick={() => navigate("/sysadmin/gohighlevel/agencies")}
        />
        <StatCard
          value={data.subAccounts.totalSubAccounts.toLocaleString()}
          label="Sub-accounts"
          sub={`${data.subAccounts.activeSubAccounts} active`}
          onClick={() => navigate("/sysadmin/gohighlevel/locations")}
        />
        <StatCard
          value={data.subAccounts.totalContacts.toLocaleString()}
          label="Contacts"
          onClick={() => navigate("/sysadmin/gohighlevel/locations")}
        />
        <StatCard
          value={data.bulkScrubEnabled.toLocaleString()}
          label="Bulk scrub enabled"
          sub={data.runningNow > 0 ? `${data.runningNow} running now` : "none running now"}
          accent={data.runningNow > 0 ? "accent" : undefined}
          onClick={() => navigate("/sysadmin/gohighlevel/locations?bulkScrub=1")}
        />
        <StatCard
          value={scrubbedToday.toLocaleString()}
          label="Contacts scrubbed today"
          sub={`${data.today.singleScrubsToday} single · ${data.today.bulkProcessedToday} in bulk`}
          accent="accent"
        />
        <StatCard
          value={data.today.bulkCompletedToday.toLocaleString()}
          label="Bulk runs completed today"
          sub={`${data.today.bulkUpdatedToday.toLocaleString()} contacts updated`}
        />
        {data.today.bulkFailedToday > 0 && (
          <StatCard
            value={data.today.bulkFailedToday.toLocaleString()}
            label="Bulk runs failed today"
            accent="warn"
          />
        )}
      </div>

      <div className={styles.sectionTitle}>Recent bulk scrub runs</div>
      {data.recentRuns.length === 0 ? (
        <div className={styles.emptyState}>No bulk scrub runs yet.</div>
      ) : (
        <table className={styles.miniTable}>
          <thead>
            <tr>
              <th>Agency</th>
              <th>Sub-account</th>
              <th>Status</th>
              <th style={{ textAlign: "right" }}>Processed</th>
              <th style={{ textAlign: "right" }}>Updated</th>
              <th style={{ textAlign: "right" }}>Skipped</th>
              <th>Started</th>
              <th>Completed</th>
            </tr>
          </thead>
          <tbody>
            {data.recentRuns.map((r) => (
              <tr key={r.id}>
                <td>{r.agencyName}</td>
                <td>
                  <button
                    className={styles.linkButton}
                    onClick={() => navigate(`/sysadmin/gohighlevel/locations/${r.locationId}`)}
                  >
                    {r.locationName || r.locationId}
                  </button>
                </td>
                <td><span className={`badge ${STATUS_CLASS[r.status] ?? "bg-secondary"}`}>{STATUS_LABEL[r.status] ?? "Unknown"}</span></td>
                <td style={{ textAlign: "right" }}>{r.processedCount?.toLocaleString() ?? "—"}</td>
                <td style={{ textAlign: "right" }}>{r.updatedCount?.toLocaleString() ?? "—"}</td>
                <td style={{ textAlign: "right" }}>{r.skippedCount?.toLocaleString() ?? "—"}</td>
                <td>{new Date(r.startedAt).toLocaleString()}</td>
                <td>{r.completedAt ? new Date(r.completedAt).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default Dashboard;
